import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import type { LobApprovalFormValues } from "../types/LobApproval";
import FormNumberFieldV2 from "@components/FormsV2/FormNumberFieldV2/FormNumberFieldV2";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import "./style.css";

export interface SectionOption {
  id: string;
  name: string;
}

export interface LobApprovalFormProps {
  id: string;
  initialValues: LobApprovalFormValues;
  availableSections: SectionOption[]; // remaining_lobs logic from caller (Sections minus LobApprovals already covering them; plus current section on edit)
  onSubmit: (values: LobApprovalFormValues) => void;
  isSubmitting?: boolean;
}

export const LobApprovalForm = ({
  id,
  initialValues,
  availableSections,
  onSubmit,
  isSubmitting: _isSubmitting,
}: LobApprovalFormProps) => {
  const [values, setValues] = useState<LobApprovalFormValues>(initialValues);
  const update = <K extends keyof LobApprovalFormValues>(
    key: K,
    val: LobApprovalFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: val }));

  return (
    <Form
      id={id}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(values);
      }}
    >
      <div className="row">
        <div className="col-sm-4">
          <RequiredReactSelect
            label="* Line of Business"
            value={
              availableSections.find((s) => s.id === values.sectionId)
                ? {
                    value: values.sectionId as string,
                    label: availableSections.find(
                      (s) => s.id === values.sectionId
                    )!.name,
                  }
                : undefined
            }
            onChange={(opt: any) =>
              update("sectionId", opt ? String(opt.value) : null)
            }
            options={availableSections.map((s) => ({
              value: s.id,
              label: s.name,
            }))}
            placeholder="Select Line of Business"
            required
          />
        </div>
      </div>

      <div className="row mt-2">
        <div className="col-sm-4">
          <FormNumberFieldV2
            label="Number of Approvals Required"
            id="lob-required-approvals"
            value={values.requiredApprovals}
            min={1}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update(
                "requiredApprovals",
                e.target.value === "" ? 1 : Number(e.target.value)
              )
            }
          />
        </div>
        <div className="col-sm-4 lobApprovalToggleCell">
          <Form.Label>May Presales Engineer Also Approve Project?</Form.Label>
          <GenericToggle
            key={`smay-${initialValues.submitterMayApprove}`}
            initialValueOfIsOn={values.submitterMayApprove}
            onToggle={(v: boolean) => update("submitterMayApprove", v)}
            onText="Yes"
            offText="No"
            hasText
          />
        </div>
        <div className="col-sm-4 lobApprovalToggleCell">
          <Form.Label>Restrict Technical Approval to Team?</Form.Label>
          <GenericToggle
            key={`lim-${initialValues.limitApprovalsToTeam}`}
            initialValueOfIsOn={values.limitApprovalsToTeam}
            onToggle={(v: boolean) => update("limitApprovalsToTeam", v)}
            onText="Yes"
            offText="No"
            hasText
          />
        </div>
      </div>
    </Form>
  );
};
